import { createContext, useState } from 'react';
import {
	ThemeProvider as StyledComponentsThemeProvider,
	createGlobalStyle,
} from 'styled-components';

import { fontSize, space, themeTokens } from '@contexts/themeTokens';

const ThemeContext = createContext({
	setTheme: () => {},
	theme: null,
});

/**
 * The token values live in `./themeTokens.js` — a plain, import-free module so
 * tooling and tests can read them without pulling in styled-components. This
 * file stays the runtime seam for the legacy `theme.colors.*` and helpers. antd's
 * own theme (`src/config/antdTheme.js`) is the source of truth for new code and
 * reaches styled-components as `theme.token.*` via `AntdTokenBridge`.
 */
const darkTheme = { ...themeTokens };

export const currentTheme = darkTheme;

const GlobalStyle = createGlobalStyle`

	:root {
		--bs-body-bg: ${props => props.theme.colors.background};
		--bs-body-color: ${props => props.theme.colors.textColor};
		--bs-body-font-family: 'Montserrat', sans-serif;
		--bs-body-line-height: 1.7;

		--bs-secondary-color: ${props => props.theme.colors.textAccent};

		--bs-link-color: ${props => props.theme.colors.linkColor};
		--bs-link-color-rgb: ${props => props.theme.colors.linkColorRgb};
		--bs-link-hover-color-rgb: ${props => props.theme.colors.linkColorRgb};
	}

	a.inherit{
		color: inherit;
	}

	html, body {
		height: 100%;
		min-height: 100vh;
		margin: 0px;
    	padding: 0px;
	}

	h1, .h1 {
		font-size: ${fontSize('2xl')};
	}

	h2, .h2 {
		font-size: 18px;
	}

	.btn {
		--bs-btn-border-width: 0px;
		--bs-btn-border-radius: 5px;
		--bs-btn-font-weight: 400;

		&.show {
			background-color: ${props => props.theme.colors.backgroundAccent};
		}
		&[class*='btn-outline-'] {
			--bs-btn-border-width: 1px;
		}

		&.btn-dark{
			--bs-btn-bg: ${props => props.theme.colors.border};
		}
	}

	.primary {
		color: ${props => props.theme.colors.textColor};
		background-color: ${props => props.theme.colors.green};
		&:active, &:selected {
			background-color: ${props => props.theme.colors.green};
		}
	}

	.secondary {
		color: ${props => props.theme.colors.textColor};
		background-color: ${props => props.theme.colors.modal};
		&:hover {
			color: ${props => props.theme.colors.lightGreen};
		}
	}

	.form-control:disabled{
		background-color: ${props => props.theme.colors.backgroundGrey};
		color: ${props => props.theme.colors.textSecondary};
		opacity: 0.6;
	}

	.form-check-input {
		border-color: ${props => props.theme.colors.borderStrong};
		&:checked {
			background-color: ${props => props.theme.colors.borderStrong};
			border-color: ${props => props.theme.colors.borderStrong};
		}
	}

	.badge{
		--bs-badge-font-weight: 600;
	}

	table{
		thead{
			th{
				font-weight: ${props => props.theme.typography.weights.medium};
				color: ${props => props.theme.colors.textAccent};
				font-size: 0.9em;
				opacity: 0.7;
			}
		}
	}

	img {
		max-width: 100%;
		height: auto;
	}

	.popover{
		position: fixed;
	}

	.react-tooltip.small-tooltip{
		padding: ${space(2)} ${space(3)};
		font-size: ${fontSize('2xs')};
		line-height: 1.1em;
	}
`;

export const ThemeProvider = ({ children }) => {
	const [theme, setTheme] = useState(currentTheme);
	const value = { theme, setTheme };

	return (
		<ThemeContext.Provider value={value}>
			<StyledComponentsThemeProvider theme={theme}>
				<GlobalStyle />
				{children}
			</StyledComponentsThemeProvider>
		</ThemeContext.Provider>
	);
};
